const CARROT_QUEST_APP_ID = '61314-82851c29fbea648953851e28c5';

const CARROT_HIDDEN_ROUTE_PREFIXES = ['/blog', '/book-appointment', '/vyzov-veterinara-na-dom'] as const;

// Идемпотентный bootstrap Carrotquest: stub API, инжект внешнего скрипта,
// connect. Безопасно вызывать многократно — повторные вызовы no-op.
function bootstrapCarrotQuest(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as typeof window & {
    carrotquest?: Record<string, unknown> & { connect?: (id: string) => void };
    carrotquestasync?: unknown[];
  };
  if (w.carrotquest && typeof w.carrotquest.connect === 'function') {
    return;
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://cdn.carrotquest.app/api.min.js';
  document.head.appendChild(script);

  w.carrotquest = w.carrotquest || ({} as Record<string, unknown>);
  w.carrotquestasync = w.carrotquestasync || [];
  (w.carrotquest as { settings?: Record<string, unknown> }).settings = {};

  const methods = [
    'connect',
    'track',
    'identify',
    'auth',
    'onReady',
    'addCallback',
    'removeCallback',
    'trackMessageInteraction',
  ] as const;
  for (const method of methods) {
    (w.carrotquest as Record<string, (...args: unknown[]) => void>)[method] = function (
      ...args: unknown[]
    ) {
      (w.carrotquestasync as unknown[]).push(method, args);
    };
  }

  (w.carrotquest as { connect: (id: string) => void }).connect(CARROT_QUEST_APP_ID);
}

function matchesRoutePrefix(path: string, prefix: string): boolean {
  const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;

  return path === base || path === `${base}/` || path.startsWith(`${base}/`);
}

function shouldHideCarrotOnPath(path: string): boolean {
  return CARROT_HIDDEN_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(path, prefix));
}

export function useCarrotQuestVisibility() {
  const route = useRoute();

  const shouldHideCarrot = computed(() => shouldHideCarrotOnPath(route.path));

  return {shouldHideCarrot};
}

export function useCarrotQuestHead(layout: 'default' | 'personal') {
  const {shouldHideCarrot} = useCarrotQuestVisibility();

  useHead({
    bodyAttrs: {
      'data-layout': layout,
      'data-hide-carrot': () => (shouldHideCarrot.value ? 'true' : undefined),
    },
  });

  if (import.meta.server) {
    return;
  }

  // Откладываем загрузку Carrotquest до первого взаимодействия пользователя.
  // Чат весит ~240 KB JS + подтягивает несколько чанков, блокирует main-thread
  // на ~270 ms — большинство пользователей до него не доходит, поэтому
  // загружать его сразу не нужно.
  //
  // Fallback на requestIdleCallback намеренно не используется: на медленных
  // мобильных устройствах он отрабатывает ровно в окне TTI, стекает работу
  // в самый плохой момент и режет performance score. Если пользователь
  // вообще не скроллил и не кликал за сессию — чат ему не нужен.
  let loaded = false;
  const interactionEvents = ['scroll', 'pointerdown', 'touchstart', 'keydown'] as const;

  const tryLoad = () => {
    if (loaded) {
      return;
    }
    if (shouldHideCarrot.value) {
      return;
    }
    loaded = true;
    cleanup();
    bootstrapCarrotQuest();
  };

  const cleanup = () => {
    for (const ev of interactionEvents) {
      window.removeEventListener(ev, tryLoad);
    }
  };

  onMounted(() => {
    for (const ev of interactionEvents) {
      window.addEventListener(ev, tryLoad, { once: true, passive: true });
    }
  });

  onBeforeUnmount(cleanup);
}
